import { DEFAULT_PAGE_SIZE_NOTIFICATIONS } from '@/lib/constants/pagination';
import { ApiErrorCode, withApiErrorHandler } from '@/modules/common/api/errors';
import { axiosInstance } from '@/utils/axios';

import {
  mapNotificationFromResponse,
  mapNotificationsFromResponse,
} from '../mappers/notification.mapper';
import {
  GetNotificationsRequest,
  Notification,
  NotificationResponse,
  ReadNotificationRequest,
} from '../types';
import { RegisterNotificationTokenRequest } from '../types/request';
import {
  DeviceNotificationTokenResponse,
  UnreadNotificationsCountResponse,
} from '../types/response';

interface PaginationMetadata {
  page?: number;
  total: number;
}

export interface NotificationsPaginatedResponse {
  items: Notification[];
  pagination: PaginationMetadata;
}

/**
 * Registers a device token for push notifications
 * POST /api/notifications/device-tokens
 */
export const registerDevice = (
  data: RegisterNotificationTokenRequest
): Promise<DeviceNotificationTokenResponse> => {
  const endpoint = '/api/notifications/device-tokens';

  return withApiErrorHandler(endpoint, ApiErrorCode.DEVICE_REGISTER_FAILED, () =>
    axiosInstance.post<{
      data: DeviceNotificationTokenResponse;
      error?: { status: number; message: string; code: string };
    }>(endpoint, data)
  );
};

/**
 * Gets all registered devices for the current user
 * GET /api/notifications/device-tokens
 */
export const getDevices = (): Promise<DeviceNotificationTokenResponse[]> => {
  const endpoint = '/api/notifications/device-tokens';

  return withApiErrorHandler(endpoint, ApiErrorCode.DEVICES_LOAD_FAILED, () =>
    axiosInstance.get<{
      data: DeviceNotificationTokenResponse[];
      error?: { status: number; message: string; code: string };
    }>(endpoint)
  );
};

/**
 * Removes a registered device by ID
 * DELETE /api/notifications/device-tokens/:id
 */
export const removeDevice = (deviceId: number): Promise<void> => {
  const endpoint = `/api/notifications/device-tokens/${deviceId}`;

  return withApiErrorHandler(endpoint, ApiErrorCode.DEVICE_REMOVE_FAILED, () =>
    axiosInstance.delete<{
      data: void;
      error?: { status: number; message: string; code: string };
    }>(endpoint)
  );
};

/**
 * Removes a registered device by token
 * DELETE /api/notifications/device-tokens
 */
export const removeDeviceByToken = (token: string): Promise<void> => {
  const endpoint = '/api/notifications/device-tokens';

  return withApiErrorHandler(endpoint, ApiErrorCode.DEVICE_REMOVE_FAILED, () =>
    axiosInstance.delete<{
      data: void;
      error?: { status: number; message: string; code: string };
    }>(endpoint, {
      data: { token },
    })
  );
};

/**
 * Fetches paginated list of notifications.
 * GET /api/notifications
 */
export const getNotifications = async (
  params: GetNotificationsRequest = {}
): Promise<NotificationsPaginatedResponse> => {
  const endpoint = '/api/notifications';

  const queryParams: Record<string, string | number | boolean> = {
    pageSize: params.pageSize ?? DEFAULT_PAGE_SIZE_NOTIFICATIONS,
  };

  if (params.page !== undefined) {
    queryParams.page = params.page;
  }

  if (params.isRead !== undefined) {
    queryParams.isRead = params.isRead;
  }

  const apiData = await withApiErrorHandler(endpoint, ApiErrorCode.NOTIFICATIONS_LOAD_FAILED, () =>
    axiosInstance.get<{
      data: {
        items: NotificationResponse[];
        pagination: PaginationMetadata;
      };
      error?: { status: number; message: string; code: string };
    }>(endpoint, { params: queryParams })
  );

  return {
    items: mapNotificationsFromResponse(apiData.items),
    pagination: apiData.pagination,
  };
};

/**
 * Marks a single notification as read.
 */
export const markNotificationAsRead = async (notificationId: number): Promise<Notification> => {
  const endpoint = '/api/notifications/read';
  const requestBody: ReadNotificationRequest = { notificationId };

  const apiData = await withApiErrorHandler(
    endpoint,
    ApiErrorCode.NOTIFICATION_MARK_READ_FAILED,
    () =>
      axiosInstance.patch<{
        data: NotificationResponse;
        error?: { status: number; message: string; code: string };
      }>(endpoint, requestBody)
  );

  return mapNotificationFromResponse(apiData);
};

/**
 * Marks all notifications as read.
 */
export const markAllNotificationsAsRead = async (): Promise<void> => {
  const endpoint = '/api/notifications/read-all';

  await withApiErrorHandler(endpoint, ApiErrorCode.NOTIFICATIONS_MARK_ALL_READ_FAILED, () =>
    axiosInstance.patch<{
      data: unknown;
      error?: { status: number; message: string; code: string };
    }>(endpoint)
  );
};

/**
 * Deletes a single notification.
 * DELETE /api/notifications/:id
 */
export const deleteNotification = async (notificationId: number): Promise<void> => {
  const endpoint = `/api/notifications/${notificationId}`;

  await withApiErrorHandler(endpoint, ApiErrorCode.NOTIFICATION_DELETE_FAILED, () =>
    axiosInstance.delete<{
      data: void;
      error?: { status: number; message: string; code: string };
    }>(endpoint)
  );
};

/**
 * Deletes all notifications for the current user.
 * DELETE /api/notifications
 */
export const deleteAllNotifications = async (): Promise<void> => {
  const endpoint = '/api/notifications';

  await withApiErrorHandler(endpoint, ApiErrorCode.NOTIFICATIONS_DELETE_ALL_FAILED, () =>
    axiosInstance.delete<{
      data: void;
      error?: { status: number; message: string; code: string };
    }>(endpoint)
  );
};

/**
 * Fetches the count of unread notifications.
 * GET /api/notifications/unread-count
 */
export const getUnreadNotificationsCount = async (): Promise<number> => {
  const endpoint = '/api/notifications/unread-count';

  const apiData = await withApiErrorHandler(endpoint, ApiErrorCode.NOTIFICATIONS_LOAD_FAILED, () =>
    axiosInstance.get<{
      data: UnreadNotificationsCountResponse;
      error?: { status: number; message: string; code: string };
    }>(endpoint)
  );

  return apiData.count;
};
